import React, { useEffect, useState } from "react";
import styles from "./ProfileTeam.module.css";
import { useNavigate, useParams } from "react-router-dom";

import AddNoteIcon from "../../../../assets/icons/addNoteIconWhite.svg";
import { ReactComponent as ArrowIcon } from "../../../../assets/icons/arrowIcon.svg";
import ArrowRightIcon from "../../../../assets/icons/arrowTeamIcon.svg";
import EmailIcon from "../../../../assets/icons/emailIcon.svg";
import DepartmentIcon from "../../../../assets/icons/departmentIcon.svg";
import DateIcon from "../../../../assets/icons/evaluationIcon.svg";
import TeamLeaderIcon from "../../../../assets/icons/teamLeaderIcon.svg";
import ManagerIcon from "../../../../assets/icons/managerIcon.svg";
import ClientIcon from "../../../../assets/icons/clientIcon.svg";
import ViewIcon from "../../../../assets/icons/viewIcon.svg";
import EditIcon from "../../../../assets/icons/editIcon.svg";
import DeleteIcon from "../../../../assets/icons/deleteIcon.svg";
import ReportIcon from "../../../../assets/icons/reportIcon.svg";
import { BlueButton } from "@react-solutions-ui/findmore-ui";
import { FormOutlined } from "@ant-design/icons";

import { updateNotes, useProfile } from "../../hooks/useProfile";
import { NoteHistory, Evaluation } from "../../Interfaces/ProfileTypes";

import {
  Modal,
  ActionsContainer,
  TableComponent,
} from "@react-solutions-ui/findmore-ui";
import ViewNotePanel from "../../../../components/ui/NotePanel/ViewNotePanel";
import EditNotePanel from "../../../../components/ui/NotePanel/EditNotePanel";
import DeleteNoteModal from "../../../../components/ui/NotePanel/DeleteNoteModal/DeleteNoteModal";
import AddNotePanel from "../../../../components/ui/NotePanel/AddNotePanel";
import Loader from "../../../../components/ui/Loader";
import { Breadcrumb } from "antd";
import { useUser } from "../../../../context/UserContext";

export default function ProfileTeam() {
  const navigate = useNavigate();
  const { employeeId } = useParams<{ employeeId: string }>();
  const { data, loading } = useProfile(employeeId!);
  const { user } = useUser();
  const [showInformation, setShowInformation] = useState(true);

  const [showNotePanel, setShowNotePanel] = useState(false);
  const [successModal, setSuccessModal] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  const [selectedNote, setSelectedNote] = useState<NoteHistory | null>(null);
  const [viewNoteOpen, setViewNoteOpen] = useState(false);
  const [editNoteOpen, setEditNoteOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notesHistory, setNotesHistory] = useState<NoteHistory[]>([]);

  useEffect(() => {
    if (data) {
      setNotesHistory(data.notesHistory);
    }
  }, [data]);

  if (loading)
    return <Loader text="A carregar informações do colaborador..." />;
  if (!data) return <div className="page-container"></div>;

  const employeeName = data.employeeInfo?.name ?? "Sem nome";
  const employeeArea = data.employeeInfo?.areaName ?? "-";

  const evaluations = data.evaluations ?? [];

  const teamLeaders =
    data.mainProfile?.filter((p) => p.memberTypeLabel === "Team Leader") ?? [];
  const managers =
    data.mainProfile?.filter((p) => p.memberTypeLabel === "Manager") ?? [];
  const clients = Array.isArray(data.clientByEvaluation)
    ? data.clientByEvaluation.filter((c) => c && c.clientFullName)
    : data.clientByEvaluation && data.clientByEvaluation.clientFullName
    ? [data.clientByEvaluation]
    : [];

  const nextEvaluationDate = new Date(data.employeeInfo.nextEvaluation);
  nextEvaluationDate.setDate(nextEvaluationDate.getDate() - 30);
  const formattedDate = nextEvaluationDate.toLocaleDateString("pt-PT");

  const breadcrumbLabel =
    user?.typeLabel === "Management" || user?.typeLabel === "RH"
      ? "Colaboradores"
      : "A minha equipa";

  const MAX_VISIBLE = 4;
  const latestNotes = notesHistory.slice(0, MAX_VISIBLE);
  const showNotesViewAll = notesHistory.length > MAX_VISIBLE;

  const notesColumns = [
    {
      title: "Autor",
      dataIndex: "createdBy",
      key: "createdBy",
      onCell: () => ({ "data-label": "Autor" } as any),
      sorter: (a: NoteHistory, b: NoteHistory) =>
        a.createdBy.localeCompare(b.createdBy),
      render: (text: string, record: NoteHistory) => (
        <div className={styles.rowWithActions}>
          <span className={styles.authorText}>{text}</span>
          <ActionsContainer mobile={true}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedNote(record);
                setViewNoteOpen(true);
              }}
            >
              <img
                src={ViewIcon}
                alt="ver"
                className={styles.mobileActionIcon}
              />
            </a>

            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedNote(record);
                setEditNoteOpen(true);
              }}
            >
              <img
                src={EditIcon}
                alt="editar"
                className={styles.mobileActionIcon}
              />
            </a>

            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedNote(record);
                setDeleteModalOpen(true);
              }}
            >
              <img
                src={DeleteIcon}
                alt="eliminar"
                className={styles.mobileActionIcon}
              />
            </a>
          </ActionsContainer>
        </div>
      ),
    },
    {
      title: "Data",
      key: "createdOn",
      onCell: () => ({ "data-label": "Data" } as any),
      render: (row: NoteHistory) =>
        row.createdOn
          ? new Date(row.createdOn).toLocaleDateString("pt-PT")
          : "-",
      sorter: (a: NoteHistory, b: NoteHistory) =>
        new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
    },
    {
      title: "Categoria",
      dataIndex: "categoryName",
      key: "categoryName",
      onCell: () => ({ "data-label": "Categoria" } as any),
      render: (value: string) =>
        value ? (
          <span
            className={styles.noteCategoryTag}
            data-category={value.toUpperCase()}
          >
            {value}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Assunto",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      onCell: () => ({ "data-label": "Assunto" } as any),
    },
    {
      title: "Mensagem",
      dataIndex: "note",
      key: "note",
      ellipsis: true,
      onCell: () => ({ "data-label": "Mensagem" } as any),
    },
    {
      title: "Ações",
      key: "actions",
      className: "hide-on-mobile",
      onCell: () => ({ "data-label": "Ações" } as any),
      render: (_: any, record: NoteHistory) => (
        <ActionsContainer>
          <a
            href="#"
            className={styles.viewNote}
            onClick={(e) => {
              e.preventDefault();
              setSelectedNote(record);
              setViewNoteOpen(true);
            }}
          >
            Ver nota
          </a>
          <a
            href="#"
            className={styles.editNote}
            style={{ marginLeft: 12 }}
            onClick={(e) => {
              e.preventDefault();
              setSelectedNote(record);
              setEditNoteOpen(true);
            }}
          >
            Editar
          </a>
          <a
            href="#"
            className={styles.delete}
            style={{ marginLeft: 12 }}
            onClick={(e) => {
              e.preventDefault();
              setSelectedNote(record);
              setDeleteModalOpen(true);
            }}
          >
            Eliminar
          </a>
        </ActionsContainer>
      ),
    },
  ];

  const showReportsViewAll = evaluations.length > MAX_VISIBLE;

  const canViewInProgressReport =
    user?.typeLabel === "Management" ||
    user?.typeLabel === "RH" ||
    user?.typeLabel === "Team Leader";

  const reportColumns = [
    {
      title: "Ano",
      dataIndex: "year",
      key: "year",
      onCell: () => ({ "data-label": "Ano" } as any),
      render: (text: any, record: Evaluation) => (
        <div className={styles.rowWithActions}>
          <span className={styles.authorText}>{text}</span>
          <ActionsContainer mobile={true}>
            <a href={`/report/${record.id}`}>
              <img
                src={ReportIcon}
                alt="ver"
                className={styles.mobileActionIcon}
              />
            </a>
          </ActionsContainer>
        </div>
      ),
    },
    {
      title: "Nota final",
      key: "finalEvaluation",
      onCell: () => ({ "data-label": "Nota final" } as any),
      render: (row: Evaluation) => row.finalEvaluation?.trim() || "-",
    },
    {
      title: "Data de submissão",
      key: "submitDate",
      onCell: () => ({ "data-label": "Data de submissão" } as any),
      render: (row: Evaluation) =>
        row.statusReasonType === "Completed" && row.submitDate
          ? new Date(row.submitDate).toLocaleDateString("pt-PT")
          : "-",
    },
    {
      title: "Estado",
      key: "statusReasonType",
      onCell: () => ({ "data-label": "Estado" } as any),
      render: (_: any, row: Evaluation) => {
        let value = row.statusReasonType;
        let className = "";

        switch (value) {
          case "Not Started":
            className = styles.statusNotStarted;
            break;
          case "In Progress":
            if (row.evaluatorStatus === 122470002) {
              value = "In Evaluation";
              className = styles.statusInEvaluation;
            } else if (row.evaluatorStatus === 122470001) {
              value = "Evaluated";
              className = styles.statusEvaluated;
            } else {
              className = styles.statusInProgress;
            }
            break;
          case "Completed":
            className = styles.statusCompleted;
            break;
          default:
            className = styles.statusNotStarted;
        }

        const labelMap: Record<string, string> = {
          "Not Started": "POR INICIAR",
          "In Progress": "POR AVALIAR",
          Completed: "CONCLUÍDO",
          "In Evaluation": "EM AVALIAÇÃO",
          Evaluated: "AVALIADO",
        };

        return (
          <span className={`${styles.statusBadge} ${className}`}>
            {labelMap[value]}
          </span>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      className: "hide-on-mobile",
      onCell: () => ({ "data-label": "Ações" } as any),
      render: (_: any, record: Evaluation) => (
        <ActionsContainer>
          <button
            className={styles.evaluateButton}
            disabled={
              record.statusReasonType === "Not Started" ||
              (record.statusReasonType === "In Progress" &&
                record.evaluatorStatus === 122470001 &&
                !canViewInProgressReport)
            }
            onClick={() => {
              if (
                canViewInProgressReport &&
                record.statusReasonType === "In Progress"
              ) {
                navigate(`/report/${record.id}`);
                return;
              }

              if (
                record.evaluatorStatus === 1 &&
                record.statusReasonType === "In Progress"
              ) {
                navigate(`/questionnaire?id=${record.evaluatorId}`);
              } else {
                navigate(`/report/${record.id}`);
              }
            }}
          >
            {!canViewInProgressReport &&
            record.evaluatorStatus === 1 &&
            record.statusReasonType === "In Progress"
              ? "Avaliar"
              : "Ver relatório"}
          </button>
        </ActionsContainer>
      ),
    },
  ];

  const breadcrumbItems = [
    {
      title: (
        <a
          style={{ cursor: "pointer" }}
          onClick={() =>
            navigate(
              breadcrumbLabel === "A minha equipa"
                ? "/my-team"
                : "/all-employees"
            )
          }
        >
          {breadcrumbLabel}
        </a>
      ),
    },
    {
      title: <b> {employeeName} </b>,
    },
  ];

  const goToAllNotes = () => {
    navigate(`/profile-team/${employeeId}/notes`, {
      state: {
        notes: notesHistory,
        breadcrumbLabel,
        employeeName,
        employeeArea,
        photo: data.photo,
        employeeId: employeeId,
      },
    });
  };

  const goToAllReports = () => {
    navigate(`/profile-team/${employeeId}/reports`, {
      state: {
        reports: evaluations,
        employeeName,
        employeeArea,
        breadcrumbLabel,
        photo: data.photo,
        employeeId: employeeId,
      },
    });
  };

  return (
    <>
      <div className={styles.blueProfile}>
        <div className="page-container">
          <div className={styles.headerRow}>
            <Breadcrumb
              className={styles.breadcrumb}
              separator={<img src={ArrowRightIcon} alt="arrow" />}
              items={breadcrumbItems}
            />
          </div>

          <div className={styles.userInfo}>
            <div className={styles.userInfoLeft}>
              <div className={styles.avatarCircle}>
                {data.photo ? (
                  <img
                    src={`data:image/jpeg;base64,${atob(data.photo)}`}
                    alt="Avatar"
                  />
                ) : (
                  employeeName
                    .split(" ")
                    .map((p) => p[0])
                    .join("")
                    .toUpperCase()
                )}
              </div>
              <div className={styles.userText}>
                <h2 className={styles.userName}>{employeeName}</h2>
                <p className={styles.areaEmployee}>{employeeArea}</p>
              </div>
            </div>

            {evaluations.length > 0 &&
              (() => {
                const currentEval = evaluations[0];
                let statusValue = currentEval.statusReasonType;
                let statusClass = styles.statusNotStarted;
                if (statusValue === "In Progress") {
                  if (currentEval.evaluatorStatus === 122470002) {
                    statusValue = "In Evaluation";
                    statusClass = styles.statusInEvaluation;
                  } else if (currentEval.evaluatorStatus === 122470001) {
                    statusValue = "Evaluated";
                    statusClass = styles.statusEvaluated;
                  } else {
                    statusClass = styles.statusInProgress;
                  }
                } else if (statusValue === "Completed") {
                  statusClass = styles.statusCompleted;
                }
                const labelMap: Record<string, string> = {
                  "Not Started": "POR INICIAR",
                  "In Progress": "POR AVALIAR",
                  Completed: "CONCLUÍDO",
                  "In Evaluation": "EM AVALIAÇÃO",
                  Evaluated: "AVALIADO",
                };
                const evalDate = currentEval.submitDate
                  ? new Date(currentEval.submitDate).toLocaleDateString("pt-PT")
                  : new Date(currentEval.endDate).toLocaleDateString("pt-PT");
                return (
                  <div className={styles.headerEvalInfo}>
                    <span className={styles.headerEvalLabel}>
                      DATA DA AVALIAÇÃO – ANO ATUAL
                    </span>
                    <div className={styles.headerEvalDetails}>
                      <span className={styles.headerEvalDate}>{evalDate}</span>
                      <span className={`${styles.statusBadge} ${statusClass}`}>
                        {labelMap[statusValue]}
                      </span>
                    </div>
                  </div>
                );
              })()}
          </div>
        </div>
      </div>

      <div className={`${styles.profileContainer} page-container`}>
        {successModal.open && (
          <Modal
            title="SUCESSO"
            type="info"
            onCloseModal={() =>
              setSuccessModal({ ...successModal, open: false })
            }
            closeModalLabel="FECHAR"
          >
            {successModal.message}
          </Modal>
        )}

        <div className={styles.profileBox}>
          <div
            className={styles.infoBox}
            onClick={() => setShowInformation(!showInformation)}
          >
            <span>INFORMAÇÃO PROFISSIONAL</span>
            <span
              className={`${styles.arrow} ${
                showInformation ? styles.arrowUp : styles.arrowDown
              }`}
            >
              <ArrowIcon className={styles.arrowIcon} />
            </span>
          </div>

          {showInformation && (
            <div className={styles.detailsSection}>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <img src={EmailIcon} />
                  <div>
                    <p className={styles.label}>Email</p>
                    <p className={styles.value}>
                      {data.employeeInfo?.companyEmail ?? "-"}
                    </p>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <img src={DepartmentIcon} />
                  <div>
                    <p className={styles.label}>Departamento</p>
                    <p className={styles.value}>
                      {data.employeeInfo?.areaName ?? "-"}
                    </p>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <img src={DateIcon} />
                  <div>
                    <p className={styles.label}>Início da avaliação</p>
                    <p className={styles.value}>{formattedDate}</p>
                  </div>
                </div>

                {teamLeaders.length > 0 &&
                  teamLeaders.map((t, index) => (
                    <div className={styles.infoItem} key={`tl-${index}`}>
                      <img src={TeamLeaderIcon} />
                      <div>
                        <p className={styles.label}>Team Leader</p>
                        <p className={styles.value}>{t.employeeName}</p>
                      </div>
                    </div>
                  ))}

                {managers.length > 0 &&
                  managers.map((m, index) => (
                    <div className={styles.infoItem} key={`manager-${index}`}>
                      <img src={ManagerIcon} />
                      <div>
                        <p className={styles.label}>Manager</p>
                        <p className={styles.value}>{m.employeeName}</p>
                      </div>
                    </div>
                  ))}

                {clients.length > 0 &&
                  clients.map((c, index) => (
                    <div className={styles.infoItem} key={`client-${index}`}>
                      <img src={ClientIcon} />
                      <div>
                        <p className={styles.label}>Cliente</p>
                        <p className={styles.value}>{c.clientFullName}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.profileBox}>
          <div className={styles.notesBox}>
            <div className={styles.textBox}>
              <span className={styles.titleHistory}>HISTÓRICO DE NOTAS</span>
              <span className={styles.smallText}>
                Revê as notas anteriores do colaborador
              </span>
            </div>

            <div className={styles.buttonsBox}>
              <div className={styles.addNoteDesktop}>
                <BlueButton
                  text="ADICIONAR NOTA"
                  icon={AddNoteIcon}
                  className={`${styles.addNoteBtn} ${
                    showNotePanel ? styles.active : ""
                  }`}
                  disabled={false}
                  onClick={() => setShowNotePanel(true)}
                />
              </div>

              <button
                className={styles.addNoteMobile}
                onClick={() => setShowNotePanel(true)}
              >
                <FormOutlined
                  className={styles.addNotesIcon}
                  alt="ADICIONAR NOTA"
                />
              </button>

              {showNotesViewAll && (
                <div className={styles.viewAllDesktop} onClick={goToAllNotes}>
                  <span>VER TODOS</span>
                  <ArrowIcon className={styles.arrowLeft} />
                </div>
              )}
            </div>
          </div>

          <TableComponent
            columns={notesColumns}
            data={latestNotes}
            totalCount={notesHistory.length}
            pageSize={9}
            loading={loading}
          />

          {showNotesViewAll && (
            <div className={styles.viewAllMobile} onClick={goToAllNotes}>
              <span>VER TODOS</span>
              <ArrowIcon className={styles.arrowLeft} />
            </div>
          )}
        </div>

        <div className={styles.profileBox}>
          <div className={styles.notesBox}>
            <div className={styles.textBox}>
              <span className={styles.titleHistory}>RELATÓRIOS</span>
              <span className={styles.smallText}>
                Consulta os relatórios de desempenho do colaborador
              </span>
            </div>

            {showReportsViewAll && (
              <div className={styles.viewAllDesktop} onClick={goToAllReports}>
                <span>VER TODOS</span>
                <ArrowIcon className={styles.arrowLeft} />
              </div>
            )}
          </div>

          <TableComponent
            columns={reportColumns}
            data={evaluations.slice(0, MAX_VISIBLE)}
            totalCount={evaluations.length}
            pageSize={9}
            loading={loading}
          />

          {showReportsViewAll && (
            <div className={styles.viewAllMobile} onClick={goToAllReports}>
              <span>VER TODOS</span>
              <ArrowIcon className={styles.arrowLeft} />
            </div>
          )}
        </div>
      </div>

      {showNotePanel && (
        <AddNotePanel
          employeeId={employeeId!}
          user={employeeName}
          onClose={() => setShowNotePanel(false)}
          onSuccess={async (msg: string) => {
            setSuccessModal({
              open: true,
              message: msg,
            });
            var newdata = await updateNotes(employeeId!);
            if (newdata) {
              setNotesHistory(newdata?.notesHistory);
            }
          }}
        />
      )}

      {viewNoteOpen && selectedNote && (
        <ViewNotePanel
          note={selectedNote}
          employeeId={employeeId!}
          user={employeeName}
          onClose={() => setViewNoteOpen(false)}
        />
      )}

      {editNoteOpen && selectedNote && (
        <EditNotePanel
          note={selectedNote}
          employeeId={employeeId!}
          user={employeeName}
          onClose={() => setEditNoteOpen(false)}
          onSuccessEdit={async (msg: string) => {
            setSuccessModal({
              open: true,
              message: msg,
            });
            var newdata = await updateNotes(employeeId!);
            if (newdata) {
              setNotesHistory(newdata.notesHistory);
            }
          }}
        />
      )}

      {deleteModalOpen && selectedNote && (
        <DeleteNoteModal
          noteId={selectedNote.id}
          employeeId={employeeId!}
          onClose={() => setDeleteModalOpen(false)}
          onSuccess={async (msg) => {
            setSuccessModal({
              open: true,
              message: msg,
            });
            var newdata = await updateNotes(employeeId!);
            if (newdata) {
              setNotesHistory(newdata.notesHistory);
            }
          }}
        />
      )}
    </>
  );
}
